/**
 * Contract PDF template: generates PDFs from contract content (header, paragraphs, consent blocks, signature).
 * Used by Shield, Boost, and payout contracts. Header logo resolved from dashboards by contract.header.logo (navigation_tag) and user franchise_id.
 */
import enfinityLogo from "@/assets/images/enfinity-logo.png";
import ceoSignature from "@/assets/images/sgntr.png";
import jsPDF from "jspdf";

// ——— Defaults & assets ———
const DEFAULT_MAX_WIDTH = 170;
const DEFAULT_CONTRACT_ASSETS = { logo: enfinityLogo, ceoSignature };

// ——— Logo helpers ———
function isSvgLogo(src) {
  if (typeof src !== "string" || !src.trim()) return false;
  const s = src.trim().toLowerCase();
  return s.endsWith(".svg") || s.startsWith("data:image/svg+xml") || s.includes("<svg");
}

function isPngDataUrl(src) {
  return typeof src === "string" && src.trim().toLowerCase().startsWith("data:image/png;base64,");
}

async function convertSvgToPngDataUrl(svgSource) {
  let dataUrl = svgSource;
  if (typeof svgSource === "string" && (svgSource.startsWith("http://") || svgSource.startsWith("https://"))) {
    try {
      const res = await fetch(svgSource, { mode: "cors" });
      if (!res.ok) return null;
      const text = await res.text();
      const base64 = btoa(unescape(encodeURIComponent(text)));
      dataUrl = `data:image/svg+xml;base64,${base64}`;
    } catch {
      return null;
    }
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width || 200;
        canvas.height = img.naturalHeight || img.height || 60;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

async function ensureLogoPngDataUrl(logoSource) {
  if (!logoSource || typeof logoSource !== "string") return null;
  const s = logoSource.trim();
  if (isPngDataUrl(s)) return s;
  if (isSvgLogo(s)) return await convertSvgToPngDataUrl(s);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width || 200;
        canvas.height = img.naturalHeight || img.height || 60;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = s;
  });
}

// ——— Page & layout constants (mm) ———
const PAGE_HEIGHT = 297;
const PAGE_WIDTH = 210;
const HEADER_LOGO_HEIGHT = 6;
const HEADER_LOGO_MAX_WIDTH = 80;
const MARGIN_TOP = 0;
const MARGIN_BOTTOM = 10;
const LINE_HEIGHT_FACTOR = -0.55;
const PARAGRAPH_SPACING = 4;
const HEADER_TITLE_Y = 36;
const HEADER_SUBTITLE_Y = 46;
const CONTENT_START_Y_BELOW_HEADER = 48;
const GAP_AFTER_SUBTITLE = 15;
const NEW_PAGE_START_Y = 25;
const SIGNATURE_SECTION_START_Y = 20;
const GAP_BEFORE_SIGNATURE = 28;
const SIGNATURE_SECTION_HEIGHT = 155;
const GAP_BEFORE_CONSENT_BLOCK = 4;
const CONSENT_QUESTION_ANSWER_GAP = 14;

// ——— Context & text helpers ———
function getImageDimensions(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width || 1;
      const h = img.naturalHeight || img.height || 1;
      resolve({ width: w, height: h });
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

function interpolate(text, context = {}) {
  if (typeof text !== "string") return text;
  return text.replace(/\{\{([^}]+)\}\}/g, (_, path) => {
    const keys = path.trim().split(".");
    let value = context;
    for (const key of keys) {
      value = value?.[key];
    }
    return value != null ? String(value) : "";
  });
}

function getByPath(obj, path) {
  if (!path || typeof path !== "string") return undefined;
  return path.trim().split(".").reduce((acc, key) => acc?.[key], obj);
}

function highlightText(
  doc,
  text,
  x,
  y,
  highlightColor = [255, 255, 0],
  paddingX = 1,
  paddingY = 3
) {
  doc.setFillColor(...highlightColor);
  const textWidth = doc.getTextWidth(text);
  const fontSize = doc.internal.getFontSize();
  const textHeight = fontSize * 0.6;
  const totalHeight = textHeight + 2 * paddingY;
  doc.rect(
    x - textWidth / 2 - paddingX,
    y - totalHeight / 2,
    textWidth + 2 * paddingX,
    totalHeight,
    "F"
  );
  doc.setTextColor(0, 0, 0);
  doc.text(text, x, y, { align: "center" });
}

function highlightTextLeft(
  doc,
  text,
  x,
  y,
  highlightColor = [255, 255, 0],
  paddingX = 1,
  paddingY = 3
) {
  doc.setFillColor(...highlightColor);
  const textWidth = doc.getTextWidth(text);
  const fontSize = doc.internal.getFontSize();
  const textHeight = fontSize * 0.6;
  const totalHeight = textHeight + 2 * paddingY;
  doc.rect(
    x - paddingX,
    y - totalHeight / 2,
    textWidth + 2 * paddingX,
    totalHeight,
    "F"
  );
  doc.setTextColor(0, 0, 0);
  doc.text(text, x, y, { align: "left" });
}

// ——— Paragraph & content rendering ———
function renderParagraph(doc, config, y, context) {
  const {
    text,
    fontSize = 12,
    x = 15,
    fontWeight = "normal",
    align = "left",
    highlight = false,
    maxWidth = DEFAULT_MAX_WIDTH,
    spacing,
  } = config;

  if (spacing != null && spacing > 0) {
    return { nextY: y + spacing, needsNewPage: false };
  }

  const resolvedText = interpolate(text, context);
  if (!resolvedText.trim()) return { nextY: y, needsNewPage: false };

  doc.setFontSize(fontSize);
  doc.setFont("Helvetica", fontWeight);

  const lines = doc.splitTextToSize(resolvedText, maxWidth);
  const lineHeight = fontSize * (1 + LINE_HEIGHT_FACTOR);
  const blockHeight = lines.length * lineHeight + (highlight ? lineHeight : 0);

  if (y + blockHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
    doc.addPage();
    y = NEW_PAGE_START_Y;
  }

  if (highlight) {
    const singleLine = lines.join(" ");
    const centerX = PAGE_WIDTH / 2;
    highlightText(doc, singleLine, centerX, y + lineHeight / 2);
    return {
      nextY: y + lineHeight + PARAGRAPH_SPACING,
      needsNewPage: false,
    };
  }

  const opts = { maxWidth };
  if (align === "center") {
    opts.align = "center";
    doc.text(lines, PAGE_WIDTH / 2, y, opts);
  } else {
    doc.text(lines, x, y, opts);
  }

  const nextY = y + lines.length * lineHeight + PARAGRAPH_SPACING;
  return { nextY, needsNewPage: false };
}

function renderContent(doc, paragraphs, context, startY = MARGIN_TOP) {
  let y = startY;

  for (const config of paragraphs) {
    const result = renderParagraph(doc, config, y, context);
    y = result.nextY;
    if (result.needsNewPage) {
      doc.addPage();
      y = NEW_PAGE_START_Y;
    }
  }

  return y;
}

// ——— Consent & boost rights blocks ———
const DEFAULT_SERVICE_NAME = "Enfinipay";
const AUTHORIZED_SIGNEE_SUFFIX = " Authorized Signee: Jake W. Jones, CEO";

function renderTitleYesNoBlock(doc, title, value, startY) {
  const centerX = PAGE_WIDTH / 2;
  const answerText = value ? "Yes" : "No";
  doc.setFontSize(12);
  doc.setFont("Helvetica", "bold");
  highlightText(doc, title, centerX, startY);
  doc.setFontSize(11);
  doc.setFont("Helvetica", "normal");
  doc.text(answerText, centerX, startY + CONSENT_QUESTION_ANSWER_GAP, {
    align: "center",
  });
  return startY + CONSENT_QUESTION_ANSWER_GAP + 8;
}

function getConsentBlockValue(block, context, directKey) {
  if (block?.valuePath != null && block.valuePath !== "") {
    if (block.valuePath === "true") return true;
    if (block.valuePath === "false") return false;
    const resolved = getByPath(context, block.valuePath);
    return !!resolved;
  }
  return context?.[directKey] === true;
}

function renderConsentBlock(doc, consentBlock, context, startY) {
  const { title } = consentBlock || {};
  if (!title) return startY;
  const value = getConsentBlockValue(consentBlock, context, "consentValue");
  return renderTitleYesNoBlock(doc, title, value, startY);
}

function renderBoostRightsBlock(doc, boostRightsBlock, context, startY) {
  const { title } = boostRightsBlock || {};
  if (!title) return startY;
  const value = getConsentBlockValue(boostRightsBlock, context, "boostRightsValue");
  return renderTitleYesNoBlock(doc, title, value, startY);
}

// ——— Opt-in block (after last paragraph) ———
function renderOptInBlock(doc, optInBlock, context, startY) {
  if (!optInBlock?.title || !optInBlock?.path) return startY;
  const sigX = doc.internal.pageSize.getWidth() / 15;
  let y = startY + GAP_BEFORE_CONSENT_BLOCK;
  if (y > PAGE_HEIGHT - MARGIN_BOTTOM - 40) {
    doc.addPage();
    y = NEW_PAGE_START_Y;
  }
  doc.setFontSize(12);
  doc.setFont("Helvetica", "normal");
  doc.text(optInBlock.title, sigX, y, { maxWidth: 180 });
  y += 10;
  const obj = getByPath(context, optInBlock.path);
  const labelMap = optInBlock.labelMap ?? {};
  const filter = optInBlock.filter ?? (() => true);
  const entries = Object.entries(obj || {}).filter(([k, v]) => filter(k, v));
  doc.setFontSize(11);
  for (const [key] of entries) {
    if (y > PAGE_HEIGHT - MARGIN_BOTTOM - 10) {
      doc.addPage();
      y = NEW_PAGE_START_Y;
    }
    doc.text(`• ${labelMap[key] ?? key}`, 30, y, { maxWidth: 180 });
    y += 8;
  }
  return y;
}

// ——— Signature section constants ———
const GAP_BETWEEN_ACQUIRED_AND_POST_CONSENT = 24;
const GAP_AFTER_POST_ACQUIRED_BLOCK = 6;
const POST_ACQUIRED_CONSENT_BLOCK_HEIGHT = 22;
const USER_SOCIALS_KEYS = [
  "youtube",
  "tiktok",
  "instagram",
  "facebook",
  "twitch",
  "douyin",
  "kuaishou",
  "rumble",
  "others",
];

// ——— Signature section ———
function renderSignatureSection(
  doc,
  signatureData,
  assets,
  startY,
  serviceName,
  postAcquiredConsentBlock,
  signatureContext,
  signatureBlockConfig
) {
  const { user, ipAddress, signature } = signatureData;
  const { ceoSignature } = assets || {};
  const service = serviceName ?? DEFAULT_SERVICE_NAME;
  const signeeLine = `${service}${AUTHORIZED_SIGNEE_SUFFIX}`;

  const currentDate = new Date().toLocaleDateString();
  const currentTime = new Date().toLocaleTimeString();
  const pageWidth = doc.internal.pageSize.getWidth();
  const sigX = pageWidth / 15;
  const y0 = startY != null ? startY : SIGNATURE_SECTION_START_Y;

  const hasPostAcquired = postAcquiredConsentBlock?.title;
  const blockY = hasPostAcquired ? y0 + 80 + GAP_BETWEEN_ACQUIRED_AND_POST_CONSENT : y0 + 88;
  const postAcquiredEndY = hasPostAcquired ? blockY + CONSENT_QUESTION_ANSWER_GAP + 8 : y0 + 80;
  let contentEndY = hasPostAcquired ? postAcquiredEndY + GAP_AFTER_POST_ACQUIRED_BLOCK : y0 + 115;

  doc.setFontSize(13);
  doc.setFont("Helvetica", "normal");
  doc.text("Signee's Signature Information", sigX, y0, { maxWidth: 180 });
  doc.text(`Name: ${user.first_name} ${user.last_name}`, sigX, y0 + 14, {
    maxWidth: 180,
  });
  doc.text("Signature:", sigX, y0 + 24, { maxWidth: 180 });

  if (signature) {
    doc.addImage(signature, "PNG", 20, y0 + 30, 100, 30);
  }

  doc.text(`via ${ipAddress} (${user.email})`, sigX, y0 + 65, { maxWidth: 180 });

  doc.setFontSize(14);
  highlightText(doc, `Acquired on ${currentDate} ${currentTime}`, 52, y0 + 80);

  if (hasPostAcquired) {
    doc.setFontSize(12);
    doc.setFont("Helvetica", "bold");
    highlightTextLeft(doc, postAcquiredConsentBlock.title, sigX, blockY);
    const value = getConsentBlockValue(postAcquiredConsentBlock, signatureContext, "postAcquiredConsentValue");
    doc.setFontSize(11);
    doc.setFont("Helvetica", "normal");
    doc.text(value ? "Yes" : "No", sigX, blockY + CONSENT_QUESTION_ANSWER_GAP, { maxWidth: 180 });
  }

  const hasOptInPage = signatureBlockConfig?.optInPage?.title != null && signatureBlockConfig?.optInPage?.path;

  if (signatureBlockConfig?.extraParagraphs?.length) {
    doc.setFontSize(13);
    doc.setFont("Helvetica", "normal");
    for (const config of signatureBlockConfig.extraParagraphs) {
      const result = renderParagraph(doc, config, contentEndY, signatureContext);
      contentEndY = result.nextY;
      if (result.needsNewPage) {
        doc.addPage();
        contentEndY = NEW_PAGE_START_Y;
      }
    }
  }

  if (signatureBlockConfig?.bulletList?.path) {
    const obj = getByPath(signatureContext, signatureBlockConfig.bulletList.path);
    const keys = signatureBlockConfig.bulletList.keysOrder ?? USER_SOCIALS_KEYS;
    if (obj && typeof obj === "object") {
      doc.setFontSize(11);
      doc.setFont("Helvetica", "normal");
      const bulletX = signatureBlockConfig.bulletList.x ?? 30;
      const lineGap = signatureBlockConfig.bulletList.lineGap ?? 8;
      for (const key of keys) {
        const value = obj[key];
        if (value != null && String(value).trim() !== "") {
          doc.text(`•  ${value}`, bulletX, contentEndY, { maxWidth: 180 });
          contentEndY += lineGap;
        }
      }
    }
  }

  if (hasOptInPage) {
    contentEndY += GAP_AFTER_POST_ACQUIRED_BLOCK;
    if (contentEndY > PAGE_HEIGHT - MARGIN_BOTTOM - 40) {
      doc.addPage();
      contentEndY = NEW_PAGE_START_Y;
    }
    const optIn = signatureBlockConfig.optInPage;
    doc.setFontSize(12);
    doc.setFont("Helvetica", "normal");
    doc.text(optIn.title, sigX, contentEndY, { maxWidth: 180 });
    contentEndY += 10;
    const optInObj = getByPath(signatureContext, optIn.path);
    const labelMap = optIn.labelMap ?? {};
    const filter = optIn.filter ?? (() => true);
    const entries = Object.entries(optInObj || {}).filter(([k, v]) => filter(k, v));
    doc.setFontSize(11);
    for (const [key] of entries) {
      if (contentEndY > PAGE_HEIGHT - MARGIN_BOTTOM - 10) {
        doc.addPage();
        contentEndY = NEW_PAGE_START_Y;
      }
      doc.text(`• ${labelMap[key] ?? key}`, 30, contentEndY, { maxWidth: 180 });
      contentEndY += 8;
    }
  }

  const signeeLineY = contentEndY + GAP_AFTER_POST_ACQUIRED_BLOCK;
  const ceoY = signeeLineY + 10;
  const timestampY = ceoY + 25;

  doc.setFontSize(13);
  doc.setFont("Helvetica", "bold");
  doc.text(signeeLine, sigX, signeeLineY, { maxWidth: 180 });

  if (ceoSignature) {
    doc.addImage(ceoSignature, "PNG", 15, ceoY, 100, 18);
  }

  const timestamp = new Date().toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  doc.setFontSize(10);
  doc.setFont("Helvetica", "normal");
  doc.text(`Signed on: ${timestamp}`, 22, timestampY);
}

// ——— Logo resolution (by navigation_tag + user franchise_id) ———
function resolveLogoFromDashboards(navigationTag, dashboards, user = null) {
  if (!navigationTag || !Array.isArray(dashboards) || dashboards.length === 0)
    return null;
  const tag = (navigationTag || "").toLowerCase();
  const userFranchiseId = user?.franchise_id != null ? String(user.franchise_id) : null;
  const withTag = dashboards.filter((d) => {
    const dTag = (d.navigationTag || d.navigation_tag || "").toLowerCase();
    return dTag === tag;
  });
  if (withTag.length === 0) return null;
  const dFranchiseStr = (d) => {
    const id = d.franchiseId ?? d.franchise_id;
    return id != null ? String(id) : null;
  };
  if (userFranchiseId != null) {
    const match = withTag.find((d) => dFranchiseStr(d) === userFranchiseId);
    if (match) {
      const fullLogo = match.fullLogo ?? (match.full_logo && (typeof match.full_logo === "string" ? match.full_logo : match.full_logo.sas_url));
      return fullLogo || null;
    }
    return null;
  }
  const globalMatch = withTag.find((d) => dFranchiseStr(d) == null);
  if (!globalMatch) return null;
  const fullLogo = globalMatch.fullLogo ?? (globalMatch.full_logo && (typeof globalMatch.full_logo === "string" ? globalMatch.full_logo : globalMatch.full_logo.sas_url));
  return fullLogo || null;
}

// ——— Main entry ———
export async function generateContractPdf(contract, options = {}) {
  const {
    context = {},
    signatureData = null,
    assets = {},
    fileName = "Contract.pdf",
    addSignatureSection = true,
    logoFromDashboard,
  } = options;

  const doc = new jsPDF();
  const mergedAssets = { ...DEFAULT_CONTRACT_ASSETS, ...assets };

  const now = new Date();
  const defaultContext = {
    currentDate: now.toLocaleDateString(),
    currentTime: now.toLocaleTimeString(),
    ...context,
    ...(signatureData?.user != null ? { user: signatureData.user } : {}),
  };

  const headerLogoTag = contract.header?.logo;
  let headerLogo = mergedAssets.logo;
  if (headerLogoTag && logoFromDashboard?.dashboards?.length) {
    const userForLogo =
      logoFromDashboard.user ?? signatureData?.user ?? context.user ?? null;
    const resolved = resolveLogoFromDashboards(
      headerLogoTag,
      logoFromDashboard.dashboards,
      userForLogo
    );
    if (resolved) headerLogo = resolved;
  }

  if (headerLogo) {
    const pngDataUrl = await ensureLogoPngDataUrl(headerLogo);
    headerLogo = pngDataUrl || headerLogo;
  }

  const hasHeader = contract.header && Object.keys(contract.header).length > 0;
  const { title, subtitle } = contract.header || {};
  const contentStartY = hasHeader
    ? (subtitle ? HEADER_SUBTITLE_Y + GAP_AFTER_SUBTITLE : CONTENT_START_Y_BELOW_HEADER)
    : MARGIN_TOP;

  if (hasHeader) {
    if (headerLogo && isPngDataUrl(headerLogo)) {
      const dims = await getImageDimensions(headerLogo);
      const imgW = dims?.width ?? 65;
      const imgH = dims?.height ?? 6;
      const displayHeight = HEADER_LOGO_HEIGHT;
      let displayWidth = displayHeight * (imgW / imgH);
      if (displayWidth > HEADER_LOGO_MAX_WIDTH) displayWidth = HEADER_LOGO_MAX_WIDTH;
      const x = (PAGE_WIDTH - displayWidth) / 2;
      doc.addImage(headerLogo, "PNG", x, 15, displayWidth, displayHeight);
    }
    doc.setFontSize(16);
    doc.setFont("Helvetica", "bold");
    if (title) {
      doc.text(interpolate(title, defaultContext), PAGE_WIDTH / 2, HEADER_TITLE_Y, {
        align: "center",
      });
    }
    if (subtitle) {
      doc.text(interpolate(subtitle, defaultContext), PAGE_WIDTH / 2, HEADER_SUBTITLE_Y, {
        align: "center",
      });
    }
  }

  const paragraphs = contract.paragraphs || [];
  const hasEarlyBlocks =
    (contract.consentBlock || contract.boostRightsBlock) && paragraphs.length > 0;
  const introParagraphCount = contract.paragraphsBeforeConsent ?? 1;
  let contentEndY;

  if (hasEarlyBlocks) {
    contentEndY = renderContent(
      doc,
      paragraphs.slice(0, introParagraphCount),
      defaultContext,
      contentStartY
    );

    if (contract.consentBlock) {
      contentEndY += GAP_BEFORE_CONSENT_BLOCK;
      if (contentEndY > PAGE_HEIGHT - MARGIN_BOTTOM - 30) {
        doc.addPage();
        contentEndY = NEW_PAGE_START_Y;
      }
      contentEndY = renderConsentBlock(
        doc,
        contract.consentBlock,
        defaultContext,
        contentEndY
      );
    }
    if (contract.boostRightsBlock) {
      contentEndY += GAP_BEFORE_CONSENT_BLOCK;
      if (contentEndY > PAGE_HEIGHT - MARGIN_BOTTOM - 30) {
        doc.addPage();
        contentEndY = NEW_PAGE_START_Y;
      }
      contentEndY = renderBoostRightsBlock(
        doc,
        contract.boostRightsBlock,
        defaultContext,
        contentEndY
      );
    }

    contentEndY = renderContent(
      doc,
      paragraphs.slice(introParagraphCount),
      defaultContext,
      contentEndY
    );
  } else {
    contentEndY = renderContent(doc, paragraphs, defaultContext, contentStartY);
    if (contract.consentBlock) {
      contentEndY += GAP_BEFORE_CONSENT_BLOCK;
      if (contentEndY > PAGE_HEIGHT - MARGIN_BOTTOM - 30) {
        doc.addPage();
        contentEndY = NEW_PAGE_START_Y;
      }
      contentEndY = renderConsentBlock(
        doc,
        contract.consentBlock,
        defaultContext,
        contentEndY
      );
    }
    if (contract.boostRightsBlock) {
      contentEndY += GAP_BEFORE_CONSENT_BLOCK;
      if (contentEndY > PAGE_HEIGHT - MARGIN_BOTTOM - 30) {
        doc.addPage();
        contentEndY = NEW_PAGE_START_Y;
      }
      contentEndY = renderBoostRightsBlock(
        doc,
        contract.boostRightsBlock,
        defaultContext,
        contentEndY
      );
    }
  }

  if (contract.optInBlock?.title && contract.optInBlock?.path) {
    contentEndY = renderOptInBlock(
      doc,
      contract.optInBlock,
      defaultContext,
      contentEndY
    );
  }

  if (addSignatureSection && signatureData) {
    const postAcquiredConsent = contract.signatureBlock?.postAcquiredConsent;
    const signatureHeight =
      SIGNATURE_SECTION_HEIGHT +
      (postAcquiredConsent?.title ? POST_ACQUIRED_CONSENT_BLOCK_HEIGHT : 0);
    let signatureStartY = contentEndY + GAP_BEFORE_SIGNATURE;
    if (signatureStartY + signatureHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
      doc.addPage();
      signatureStartY = NEW_PAGE_START_Y;
    }
    const serviceName = contract.signatureBlock?.serviceName;
    renderSignatureSection(
      doc,
      signatureData,
      mergedAssets,
      signatureStartY,
      serviceName,
      postAcquiredConsent,
      defaultContext,
      contract.signatureBlock
    );
  }

  const pdfBlob = doc.output("blob");
  const resolvedFileName =
    typeof fileName === "function" ? fileName(defaultContext) : fileName;
  return new File([pdfBlob], resolvedFileName, {
    type: "application/pdf",
  });
}

export {
  interpolate,
  getByPath,
  resolveLogoFromDashboards,
  renderParagraph,
  renderContent,
  renderTitleYesNoBlock,
  renderConsentBlock,
  renderBoostRightsBlock,
  renderSignatureSection,
  DEFAULT_MAX_WIDTH,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  MARGIN_TOP,
  MARGIN_BOTTOM,
  PARAGRAPH_SPACING,
  LINE_HEIGHT_FACTOR,
  CONTENT_START_Y_BELOW_HEADER,
  NEW_PAGE_START_Y,
};
